import { caseApiRequest } from './apiClient';
import type { ApiResult } from './types';

export type CaseRequestLetterTemplateContent = {
  id: string;
  code: string;
  name: string;
  description: string;
  letterContent: string;
  categoryId?: string;
  subCategoryId?: string;
};

export type CaseRequestLetterTemplatesContent = {
  caseRequestId: string;
  templates: CaseRequestLetterTemplateContent[];
};

export type CaseRequestDraftLetter = {
  id: string;
  caseRequestId: string;
  letterTemplateId: string;
  content: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export const DRAFT_LETTER_NO_TEMPLATES_MESSAGE = 'No letter template available.';

export function isNoLetterTemplatesError(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  return (
    normalized.includes('no active letter template') ||
    normalized.includes('letter template response is invalid')
  );
}

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeTemplate(raw: unknown): CaseRequestLetterTemplateContent | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = strField(r, 'id');
  const name = strField(r, 'name');
  const letterContent = strField(r, 'letterContent', 'letter_content');
  if (!id || !name) return null;
  const categoryId = strField(r, 'categoryId', 'category_id');
  const subCategoryId = strField(r, 'subCategoryId', 'sub_category_id');
  return {
    id,
    code: strField(r, 'code'),
    name,
    description: strField(r, 'description'),
    letterContent,
    ...(categoryId ? { categoryId } : {}),
    ...(subCategoryId ? { subCategoryId } : {}),
  };
}

function normalizeTemplatesResponse(raw: unknown): CaseRequestLetterTemplatesContent | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const caseRequestId = strField(r, 'caseRequestId', 'case_request_id');
  const templatesRaw = r.templates;
  if (!caseRequestId || !Array.isArray(templatesRaw)) return null;

  const templates = templatesRaw
    .map(normalizeTemplate)
    .filter((tpl): tpl is CaseRequestLetterTemplateContent => tpl !== null);
  if (templates.length === 0) return null;

  return { caseRequestId, templates };
}

function normalizeDraftLetter(raw: unknown): CaseRequestDraftLetter | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = strField(r, 'id');
  const caseRequestId = strField(r, 'caseRequestId', 'case_request_id');
  const letterTemplateId = strField(r, 'letterTemplateId', 'letter_template_id');
  if (!id || !caseRequestId || !letterTemplateId) return null;
  return {
    id,
    caseRequestId,
    letterTemplateId,
    content: strField(r, 'content'),
    createdAt: strField(r, 'createdAt', 'created_at'),
    createdBy: strField(r, 'createdBy', 'created_by'),
    updatedAt: strField(r, 'updatedAt', 'updated_at'),
    updatedBy: strField(r, 'updatedBy', 'updated_by'),
  };
}

/** CMS-CRC-24 — GET /v1/case-requests/{caseRequestId}/letter-template/content */
export async function getCaseRequestLetterTemplateContent(
  caseRequestId: string,
  signal?: AbortSignal,
): Promise<ApiResult<CaseRequestLetterTemplateContent>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/letter-template/content`, { signal });
  if (!res.ok) return { success: false, message: res.message };

  const tpl = normalizeTemplate(res.data);
  if (!tpl) return { success: false, message: 'Letter template response is invalid.' };
  return { success: true, data: tpl };
}

/** CMS-CRC-24-2 — GET /v2/case-requests/{caseRequestId}/letter-template/content */
export async function getCaseRequestLetterTemplatesContent(
  caseRequestId: string,
  signal?: AbortSignal,
): Promise<ApiResult<CaseRequestLetterTemplatesContent>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(
    `/v2/case-requests/${encodeURIComponent(id)}/letter-template/content`,
    { signal },
  );
  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeTemplatesResponse(res.data);
  if (!data) return { success: false, message: 'Letter template response is invalid.' };
  return { success: true, data };
}

/** CMS-CRC-25 — GET /v1/case-requests/{caseRequestId}/draft-letter */
export async function getCaseRequestDraftLetter(
  caseRequestId: string,
  signal?: AbortSignal,
): Promise<ApiResult<CaseRequestDraftLetter | null>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/draft-letter`, { signal });
  if (!res.ok) return { success: false, message: res.message };

  if (res.data == null) return { success: true, data: null };

  const draft = normalizeDraftLetter(res.data);
  if (!draft) return { success: false, message: 'Draft letter response is invalid.' };
  return { success: true, data: draft };
}

/** CMS-CRC-23 — POST /v1/case-requests/draft-letter */
export async function createCaseRequestDraftLetter(input: {
  caseRequestId: string;
  letterTemplateId: string;
  content: string;
  draftLetterId?: string;
}): Promise<ApiResult<CaseRequestDraftLetter>> {
  const caseRequestId = input.caseRequestId.trim();
  const letterTemplateId = input.letterTemplateId.trim();
  const content = input.content;
  const draftLetterId = (input.draftLetterId ?? '').trim();

  if (!caseRequestId) return { success: false, message: 'Case request id is required.' };
  if (!letterTemplateId) return { success: false, message: 'Letter template id is required.' };
  if (!content.trim()) return { success: false, message: 'Letter content is required.' };

  const res = await caseApiRequest<unknown>('/draft-letter', {
    method: 'POST',
    body: {
      draftLetterId: draftLetterId || undefined,
      caseRequestId,
      letterTemplateId,
      content,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  const saved = normalizeDraftLetter(res.data);
  if (!saved) return { success: false, message: 'Draft letter response is invalid.' };
  return { success: true, data: saved };
}

